import { useCallback, useMemo, useState } from 'react';
import {
  LifeCycleTransition,
  TransitionLifeCycleType,
  TransitionMoveDirection,
  TransitionRotateDirection,
  TransitionType
} from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.types';
import { getElementTransitionByLifeCycleType } from '@/player-config/src/utils/playerConfigUtils';
import {
  getDefaultTransition,
  getElementDurationInMs
} from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transition.utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import {
  TRANSITION_MAXIMUM_DURATION,
  TRANSITION_MINIMUM_DURATION
} from '@/player-config/src/types/RemotionPlayerConfigTypes/Animations/Transitions/Transitions.constants';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';

import { ImageElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/ImageElement';
import { VideoElement } from '@/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import useAnalytics from '@/hooks/useAnalytics';

export const useTransitionPopup = ({ selectedElement }: { selectedElement: ImageElement | VideoElement }) => {
  const elementId = useMemo(() => {
    return selectedElement.id;
  }, [selectedElement]);

  const { fps, clipId } = useClipsContext();
  const {
    trackTransitionLifeCycleSelect,
    trackTransitionTypeSelect,
    trackTransitionMoveDirectionSelect,
    trackTransitionRotateDirectionSelect,
    trackTransitionDurationUpdate
  } = useAnalytics();

  const parentDuration = useMemo(() => {
    return getElementDurationInMs(selectedElement, fps);
  }, [selectedElement, fps]);

  const isElementDurationTooSmall = useMemo(() => {
    return parentDuration < TRANSITION_MINIMUM_DURATION;
  }, [parentDuration]);

  const [transitionLifeCycle, setTransitionLifeCycle] = useState<TransitionLifeCycleType>(TransitionLifeCycleType.IN);

  const currentTransition = useMemo(() => {
    return getElementTransitionByLifeCycleType(selectedElement, transitionLifeCycle);
  }, [transitionLifeCycle, selectedElement]);

  const onClickTransitionLifeCycleTab = useCallback(
    (lifeCycle: TransitionLifeCycleType) => {
      trackTransitionLifeCycleSelect({ clipId, assetType: selectedElement.type, lifeCycleType: lifeCycle });
      setTransitionLifeCycle(lifeCycle);
    },
    [trackTransitionLifeCycleSelect, clipId, selectedElement.type]
  );
  const updateTransitionType = useCallback(
    (transitionType: TransitionType | null) => {
      if (transitionType === null) {
        trackTransitionTypeSelect({
          clipId,
          assetType: selectedElement.type,
          lifeCycleType: transitionLifeCycle,
          transitionType,
          mode: 'REMOVE'
        });
        updateClipData({
          clipId,
          actionType: CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION,
          elementId,
          transitionLifeCycle,
          updateLifeCycleTransition: null
        });
        return;
      }
      if (!currentTransition) {
        trackTransitionTypeSelect({
          clipId,
          assetType: selectedElement.type,
          lifeCycleType: transitionLifeCycle,
          transitionType,
          mode: 'INSERT'
        });
        updateClipData({
          clipId,
          actionType: CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION,
          elementId,
          transitionLifeCycle,
          updateLifeCycleTransition: getDefaultTransition(selectedElement, fps, transitionType)
        });
        return;
      }
      const updatedTransition = getDefaultTransition(selectedElement, fps, transitionType);
      updatedTransition.properties.duration = currentTransition.properties.duration;
      trackTransitionTypeSelect({
        clipId,
        assetType: selectedElement.type,
        lifeCycleType: transitionLifeCycle,
        transitionType,
        mode: 'UPDATE'
      });
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION,
        elementId,
        transitionLifeCycle,
        updateLifeCycleTransition: updatedTransition
      });
    },
    [clipId, elementId, fps, transitionLifeCycle, selectedElement, currentTransition, trackTransitionTypeSelect]
  );
  const updateTransitionRotateDirection = useCallback(
    (rotateDirection: TransitionRotateDirection) => {
      if (currentTransition?.type !== TransitionType.ROTATE) {
        throw new Error('Current transition is not a rotate transition');
      }
      const rotateTransition = currentTransition;
      const updatedTransition = {
        ...rotateTransition,
        properties: { ...rotateTransition.properties, rotateDirection }
      };
      trackTransitionRotateDirectionSelect({
        clipId,
        assetType: selectedElement.type,
        lifeCycleType: transitionLifeCycle,
        direction: rotateDirection
      });
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION,
        elementId,
        transitionLifeCycle,
        updateLifeCycleTransition: updatedTransition
      });
    },
    [
      clipId,
      elementId,
      transitionLifeCycle,
      currentTransition,
      trackTransitionRotateDirectionSelect,
      selectedElement.type
    ]
  );
  const updateTransitionMoveDirection = useCallback(
    (moveDirection: TransitionMoveDirection) => {
      if (currentTransition?.type !== TransitionType.MOVE) {
        throw new Error('Current transition is not a move transition');
      }
      const moveTransition = currentTransition;
      const updatedTransition = {
        ...moveTransition,
        properties: { ...moveTransition.properties, moveDirection }
      };
      trackTransitionMoveDirectionSelect({
        clipId,
        assetType: selectedElement.type,
        lifeCycleType: transitionLifeCycle,
        direction: moveDirection
      });
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION,
        elementId,
        transitionLifeCycle,
        updateLifeCycleTransition: updatedTransition
      });
    },
    [
      clipId,
      elementId,
      transitionLifeCycle,
      currentTransition,
      trackTransitionMoveDirectionSelect,
      selectedElement.type
    ]
  );

  const updateTransitionDuration = useCallback(
    (duration: number) => {
      if (!currentTransition) {
        throw new Error('Transition does not exist');
      }
      const updatedTransition = {
        ...currentTransition,
        properties: { ...currentTransition.properties, duration }
      } as LifeCycleTransition;
      trackTransitionDurationUpdate({
        clipId,
        assetType: selectedElement.type,
        transitionLifeCycle,
        transitionType: currentTransition.type,
        duration
      });
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPSERT_ELEMENT_TRANSITION,
        elementId,
        transitionLifeCycle,
        updateLifeCycleTransition: updatedTransition
      });
    },
    [clipId, elementId, transitionLifeCycle, selectedElement.type, currentTransition, trackTransitionDurationUpdate]
  );

  const maxDuration = useMemo(() => {
    return Math.min(parentDuration, TRANSITION_MAXIMUM_DURATION);
  }, [parentDuration]);
  return {
    parentDuration,
    maxDuration,
    isElementDurationTooSmall,
    transitionLifeCycle,
    currentTransition,
    onClickTransitionLifeCycleTab,
    updateTransitionType,
    updateTransitionRotateDirection,
    updateTransitionMoveDirection,
    updateTransitionDuration
  };
};
